import { useState } from 'react';
import { useList } from '@refinedev/core';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { ReportFilterBar } from '../../../components/ReportFilterBar.js';
import { MiniBarChart } from '../../../components/charts/MiniBarChart.js';
import { humanizeEnum } from '../../../components/Form.js';

interface VacancyRow {
  unitId: string;
  propertyName: string;
  unitLabel: string;
  unitStatus: string;
  marketRent: string;
  vacantSince: string | null;
  daysVacant: number;
}

const columns: Column<VacancyRow>[] = [
  { key: 'property', header: 'Property', render: (r) => r.propertyName },
  { key: 'unit', header: 'Unit', render: (r) => r.unitLabel },
  { key: 'status', header: 'Status', render: (r) => humanizeEnum(r.unitStatus) },
  { key: 'rent', header: 'Market rent', numeric: true, render: (r) => `$${r.marketRent}` },
  { key: 'since', header: 'Vacant since', render: (r) => r.vacantSince ?? '—' },
  {
    key: 'days',
    header: 'Days vacant',
    numeric: true,
    render: (r) => (
      <span className={r.daysVacant > 30 ? 'pill pill--late' : 'pill'}>{r.daysVacant}d</span>
    ),
  },
];

/** Same 0–30/31–60/61–90/90+ severity buckets as AR aging — days-vacant and
 *  days-late are the same kind of clock, so reusing the boundaries keeps
 *  "what does 90+ mean" answered the same way across the whole reports pane. */
function byDaysVacant(rows: VacancyRow[]) {
  const buckets = { '0–30d': 0, '31–60d': 0, '61–90d': 0, '90+d': 0 };
  for (const row of rows) {
    if (row.daysVacant <= 30) buckets['0–30d'] += 1;
    else if (row.daysVacant <= 60) buckets['31–60d'] += 1;
    else if (row.daysVacant <= 90) buckets['61–90d'] += 1;
    else buckets['90+d'] += 1;
  }
  return Object.entries(buckets).map(([label, value]) => ({
    label,
    value,
    displayValue: String(value),
  }));
}

export function Vacancy({
  ownerId,
  onOwnerChange,
}: {
  ownerId: string;
  onOwnerChange: (value: string) => void;
}) {
  const [propertyId, setPropertyId] = useState('');

  const { data, isLoading, error } = useList<VacancyRow>({
    resource: 'reports/vacancy',
    pagination: { mode: 'off' },
    filters: [
      { field: 'propertyId', operator: 'eq', value: propertyId || undefined },
      { field: 'ownerId', operator: 'eq', value: ownerId || undefined },
    ],
  });

  const rows = data?.data ?? [];

  return (
    <div className="stack">
      <p className="muted">
        Units not currently occupied, worst first. Days vacant is measured from the most recent
        move-out on that unit — this is what owners ask about.
      </p>

      <ReportFilterBar
        propertyId={propertyId}
        onPropertyChange={setPropertyId}
        ownerId={ownerId}
        onOwnerChange={onOwnerChange}
        exportPath="/reports/vacancy"
        exportParams={{ propertyId: propertyId || undefined, ownerId: ownerId || undefined }}
      />

      {!isLoading && !error && rows.length > 0 ? (
        <div className="card">
          <p className="muted">Vacant units by how long, portfolio-wide.</p>
          <MiniBarChart data={byDaysVacant(rows)} colorVar="--down" severity="ascending" />
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.unitId}
        isLoading={isLoading}
        error={error}
        emptyMessage="Every unit is occupied."
      />
    </div>
  );
}
